/**
 * Centralised date/time formatting — Feature #171.
 *
 * All dates displayed to users must render in the Australia/Hobart timezone
 * (AEST +10 / AEDT +11). Previously, each component called
 * `new Date(x).toLocaleDateString('en-AU')` which used the **browser's**
 * system timezone — incorrect for users who access the platform from
 * interstate or via VPNs.
 *
 * This module exports two helpers that every component should use instead of
 * inline `toLocaleDateString` / `toLocaleString` calls. Both force
 * `timeZone: 'Australia/Hobart'` so the output is deterministic regardless of
 * the client machine's configured timezone.
 */

const TIMEZONE = 'Australia/Hobart'
const LOCALE = 'en-AU'

/**
 * Bug #206: the backend stores timestamps via `datetime.utcnow()` which
 * produces naive (timezone-unaware) ISO strings such as
 * `"2026-04-24T07:39:00.280000"` — no trailing `Z`. JavaScript's `Date`
 * constructor interprets strings without a timezone indicator as **local
 * time** (the browser's OS timezone), not UTC. For a user in Hobart the
 * local interpretation and the Hobart conversion cancel out, making the
 * raw UTC value display as-is — which is exactly the reported bug.
 *
 * This normaliser appends `Z` when the string has no timezone offset so
 * `new Date()` always parses as UTC, and the Intl formatter can then
 * correctly shift into `Australia/Hobart`.
 */
const ensureUtc = (value: string | Date): string | Date => {
  if (typeof value !== 'string') return value
  // Already has an offset indicator (Z, +HH:MM, -HH:MM)? Leave it alone.
  if (/[Zz]$/.test(value) || /[+-]\d{2}:\d{2}$/.test(value)) return value
  return value + 'Z'
}

/**
 * Format a date string (or Date) as a short date.
 * Example: "17/04/2026"
 */
export const formatDate = (value?: string | Date | null): string => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat(LOCALE, {
      timeZone: TIMEZONE,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(ensureUtc(value)))
  } catch {
    return '—'
  }
}

/**
 * Format a date string (or Date) as a full date + time.
 * Example: "17/04/2026, 2:30:00 pm"
 */
export const formatDateTime = (value?: string | Date | null): string => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat(LOCALE, {
      timeZone: TIMEZONE,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(ensureUtc(value)))
  } catch {
    return '—'
  }
}

/**
 * Format a date with a long weekday + full month name for display headers.
 * Example: "Thursday, 17 April 2026"
 */
export const formatDateLong = (value?: string | Date | null): string => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat(LOCALE, {
      timeZone: TIMEZONE,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(ensureUtc(value)))
  } catch {
    return '—'
  }
}

/**
 * Feature #187: human-friendly relative time.
 * Returns "X days ago", "X months ago", etc.
 */
export const timeAgo = (value?: string | Date | null): string => {
  if (!value) return '—'
  try {
    const date = new Date(ensureUtc(value))
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    if (diffMs < 0) return 'just now'

    const diffMins = Math.floor(diffMs / 60_000)
    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`

    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`

    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`

    const diffMonths = Math.floor(diffDays / 30)
    if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`

    const diffYears = Math.floor(diffMonths / 12)
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`
  } catch {
    return '—'
  }
}

/**
 * Format a number as AUD currency.
 * Example: formatCurrency(75000) → "$75,000"
 *
 * Bug #174: salary fields should display with currency formatting.
 * Strips trailing `.00` for whole-dollar amounts.
 */
export const formatCurrency = (
  amount?: number | null,
  currency: string = 'AUD',
): string => {
  if (amount == null) return '—'
  try {
    const formatted = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
    return formatted
  } catch {
    return String(amount)
  }
}

/**
 * Parse a pasted currency string back to a number.
 * Accepts formats like "$75,000", "75000", "$75,000.50", "75,000".
 * Returns NaN if the input can't be parsed.
 *
 * Bug #174: salary inputs need to accept pasted currency values.
 */
export const parseCurrency = (value: string): number => {
  // Strip currency symbols, commas, and whitespace.
  // ``-`` is literal at the end of a character class — no escape needed
  // (eslint no-useless-escape was rejecting the previous ``\-`` form).
  const cleaned = value.replace(/[^0-9.-]/g, '')
  return cleaned ? parseFloat(cleaned) : NaN
}
